"use strict";

/* ------------------------------------------------------------------ *
 * Mapper 2: UxROM (UNROM, UOROM, ...)
 * ------------------------------------------------------------------ *
 * Based on bfirsh/jsnes reference implementation.
 * Very simple mapper:
 * - PRG ROM: 16 KB switchable at $8000, last 16 KB fixed at $C000.
 * - CHR: Usually CHR-RAM (8 KB at $0000), but some boards have CHR-ROM.
 * - Mirroring: Hardwired vertical (iNES bit 0 = 1) or horizontal.
 * - No IRQ, no PRG RAM.
 * - Bankswitching: write any value to $8000-$FFFF, selects 16 KB bank.
 * - CHR bank select: writes to $8000-$FFFF select 8 KB CHR banks
 *   when CHR ROM is present (iNES header byte 5 specifies bank count).
 */

function register(Mappers) {
  Mappers[2] = function (nes) {
    this.nes = nes;
    // Track CHR bank for CHR-ROM boards
    this.currentVromBank = 0;
  };

  /* --------------------------------------------------------------- *
   * Mapper 2: UxROM
   * --------------------------------------------------------------- *
   * PRG ROM: 16 KB switchable at $8000, last 16 KB fixed at $C000.
   * CHR ROM: 8 KB switchable at $0000 (if present, via iNES byte 5).
   * Mirroring: Hardwired vertical/horizontal from iNES header.
   * Write $8000-$FFFF: selects PRG ROM bank (lower 4 bits) and
   *   optionally CHR ROM bank (bits 4-7 when CHR ROM present).
   * --------------------------------------------------------------- */

  Mappers[2].prototype = new Mappers[0]();

  // CNROM uses A12 rises for IRQ; UxROM has no IRQ.
  Mappers[2].prototype.irqClockRate = "none";

  Mappers[2].prototype.reset = function () {
    Mappers[0].prototype.reset.apply(this);

    this.currentVromBank = 0;
    this.prgBank = 0; // 4 bits: selects 16 KB bank at $8000
    this.mirroring = this.nes.rom.mirroring; // Use header mirroring (horizontal/vertical)

    // Initialize CHR: if CHR RAM, clear it; if CHR ROM, set first bank
    if (this.nes.rom.chrSize > 0) {
      // CHR ROM: initialize bank from header
      var num8kBanks = this.nes.rom.header[5];
      if (num8kBanks && num8kBanks > 0) {
        this.currentVromBank = 0;
        this.load8kVromBank(0, 0x0000);
      } else {
        // No CHR ROM specified; treat as CHR RAM (already loaded)
        this.loadCHRROM();
      }
    }
  };

  Mappers[2].prototype.write = function (address, value) {
    if (address < 0x8000) {
      // $6000-$7FFF: WRAM if enabled; otherwise open bus
      if (address >= 0x6000) {
        // WRAM is enabled when $8000 write sets bit 0 (some games)
        // For now, just pass through to base mapper
      }
      Mappers[0].prototype.write.apply(this, arguments);
      return;
    }

    // PRG ROM bank select: lower 4 bits (bits 0-3)
    this.prgBank = value & 0x0f;
    this.loadRomBank(this.prgBank, 0x8000);

    // CHR ROM bank select (when present): bits 4-7 select 8 KB CHR bank
    var num8kBanks = this.nes.rom.header[5];
    if (num8kBanks && num8kBanks > 0) {
      // Bits 4-7 select which 8 KB CHR bank (each 8 KB = two 4 KB banks)
      var chr8kBank = (value >> 4) & 0x0f;
      // Clamp to available 8 KB banks
      var max8kBanks = Math.min(num8kBanks, 16);
      chr8kBank = Math.min(chr8kBank, max8kBanks - 1);
      // Convert to 4 KB bank index for load8kVromBank (each 8 KB = two 4 KB)
      this.load8kVromBank(chr8kBank * 2, 0x0000);
    }
  };

  Mappers[2].prototype.loadROM = function () {
    if (!this.nes.rom.valid) {
      throw new Error("UxROM: Invalid ROM! Unable to load.");
    }

    // Load PRG-ROM:
    // First bank at $8000, last bank at $C000 (contains reset vector)
    this.loadRomBank(0, 0x8000);
    this.loadRomBank(this.nes.rom.romCount - 1, 0xc000);

    // Load CHR data:
    // If CHR ROM present (header byte 5 > 0), set initial bank
    // Otherwise, CHR RAM is already loaded by the loader
    var num8kBanks = this.nes.rom.header[5];
    if (num8kBanks && num8kBanks > 0) {
      // Initialize to first CHR ROM bank
      this.currentVromBank = 0;
      this.load8kVromBank(0, 0x0000);
    } else {
      // CHR RAM: ensure it's loaded
      this.loadCHRROM();
    }

    // Do Reset-Interrupt:
    this.nes.cpu.requestIrq(this.nes.cpu.IRQ_RESET);
  };

  Mappers[2].prototype.toJSON = function () {
    var s = Mappers[0].prototype.toJSON.apply(this);
    return s;
  };

  Mappers[2].prototype.fromJSON = function (s) {
    Mappers[0].prototype.fromJSON.apply(this, arguments);
  };
}

module.exports = { register: register };
